import { useMediaDownload } from "@/api/media";
import {
  apdFilePayloadSchema,
  APDFilePayloadSchema,
  useAPDEditBulk,
} from "@/api/trackerdata-apd";
import { FieldInput } from "@/components/form/field-input";
import { FormContainer } from "@/components/form/form-container";
import { Anchor } from "@/components/ui/anchor";
import { DownloadButton, SubmitButton } from "@/components/ui/button";
import {
  swalError,
  swalErrorDefault,
  swalLoading,
  swalSuccess,
} from "@/utils/lib/swal";
import { zodResolver } from "@hookform/resolvers/zod";
import { AxiosError } from "axios";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import APDValidationTable from "./APDValidationTable";

export function APDEditBulkUpload() {
  const form = useForm<APDFilePayloadSchema>({
    resolver: zodResolver(apdFilePayloadSchema),
  });

  const { mutate: mutateEditBulk, isPending } = useAPDEditBulk();
  const { mutate: downloadMedia, isPending: isDownloading } =
    useMediaDownload();

  const navigate = useNavigate();

  const [errorRows, setErrorRows] = useState([]);
  const [generalMessages, setGeneralMessages] = useState([]);

  function handleDownload() {
    downloadMedia(
      {
        path: "/template-master-apd/Template-Master-APD.xlsx",
        fileName: "Template-Master-APD.xlsx",
      },
      {
        onError: () => {
          swalErrorDefault.fire();
        },
      },
    );
  }

  function handleSubmit(values: APDFilePayloadSchema) {
    swalLoading.fire({
      title: "Uploading APD",
      text: "Please wait, upload process is in progress",
    });

    mutateEditBulk(
      {
        data: values,
      },
      {
        onSuccess: () => {
          setErrorRows([]);
          setGeneralMessages([]);
          swalSuccess
            .fire({
              title: "Data Upload success",
              text: "You have successfully added data",
            })
            .then(() => navigate("/master-data/apd"));
        },
        onError: (error: Error) => {
          const err = error as AxiosError;
          //@ts-expect-error error type
          const dataWarn = err.response?.data?.data;
          const errorMessage = (err.response?.data as { data: unknown })?.data;

          if (err.response?.status === 417) {
            setErrorRows(dataWarn.warnMessages);
            setGeneralMessages(dataWarn.warnMessageAllRows);
            swalError.fire({
              title: "Data Upload failed",
              text: "Please check and try again",
            });
            return;
          }

          swalError.fire({
            title: "Data Upload failed",
            text: `${errorMessage}`,
          });
        },
      },
    );
  }

  return (
    <>
      <DownloadButton onClick={handleDownload} loading={isDownloading}>
        Download Template Master APD
      </DownloadButton>
      <FormContainer form={form} onSubmit={handleSubmit}>
        <FieldInput
          name="file"
          label="Upload APD Document"
          type="file"
          accept=".xlsx, .xls"
        />
        <div className="ml-auto flex gap-x-2">
          <Anchor href="/master-data/apd" mood="axiata-blue-outline">
            Cancel
          </Anchor>
          <SubmitButton loading={isPending} />
        </div>
      </FormContainer>
      <div className="mt-10">
        <APDValidationTable
          errorRows={errorRows}
          generalMessages={generalMessages}
        />
      </div>
    </>
  );
}
